import React, { useState } from 'react';
import { 
  Calculator, Triangle, Percent, Circle, ArrowLeftRight, 
  Box, Ruler, RotateCcw, Copy, Check, StepForward, Mountain,
  Gauge, Weight, Grid3X3, Layers, ArrowUp, Building
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';

// Result Card component (moved outside to prevent recreation on render)
const ResultCard = ({ label, value, unit = '', color = 'primary' }) => (
  <div className={`bg-${color}/10 rounded-lg p-3 text-center`}>
    <p className="text-xs text-muted-foreground mb-1">{label}</p>
    <p className={`text-xl font-bold text-${color}`}>
      {value}{unit && <span className="text-sm font-normal ml-1">{unit}</span>}
    </p>
  </div>
);

const EngineeringTools = () => {
  // Pythagorean Calculator State
  const [pythA, setPythA] = useState('');
  const [pythB, setPythB] = useState('');
  const [pythC, setPythC] = useState('');
  const [pythSolveFor, setPythSolveFor] = useState('c');

  // Slope Calculator State
  const [slopeRise, setSlopeRise] = useState('');
  const [slopeRun, setSlopeRun] = useState('');
  const [slopeResult, setSlopeResult] = useState(null);

  // Stair Calculator State
  const [stairTotalRise, setStairTotalRise] = useState('');
  const [stairRiserHeight, setStairRiserHeight] = useState('7');
  const [stairTreadDepth, setStairTreadDepth] = useState('11');
  const [stairResult, setStairResult] = useState(null);

  // Ramp Calculator State
  const [rampRise, setRampRise] = useState('');
  const [rampRatio, setRampRatio] = useState('12');
  const [rampResult, setRampResult] = useState(null);

  // Concrete Calculator State
  const [concreteType, setConcreteType] = useState('slab');
  const [concreteLength, setConcreteLength] = useState('');
  const [concreteWidth, setConcreteWidth] = useState('');
  const [concreteDepth, setConcreteDepth] = useState('');
  const [concreteDiameter, setConcreteDiameter] = useState('');
  const [concreteHeight, setConcreteHeight] = useState('');
  const [concreteResult, setConcreteResult] = useState(null);

  // Unit Converter State
  const [convertValue, setConvertValue] = useState('');
  const [convertFrom, setConvertFrom] = useState('feet');
  const [convertTo, setConvertTo] = useState('meters');
  const [convertResult, setConvertResult] = useState(null);

  // Board Feet Calculator
  const [bfThickness, setBfThickness] = useState('');
  const [bfWidth, setBfWidth] = useState('');
  const [bfLength, setBfLength] = useState('');
  const [bfQuantity, setBfQuantity] = useState('1');
  const [bfResult, setBfResult] = useState(null);

  // Circle Calculator State
  const [circleInput, setCircleInput] = useState('');
  const [circleInputType, setCircleInputType] = useState('radius');
  const [circleResult, setCircleResult] = useState(null);

  // Brick/Block Calculator
  const [wallLength, setWallLength] = useState('');
  const [wallHeight, setWallHeight] = useState('');
  const [brickType, setBrickType] = useState('standard');
  const [brickResult, setBrickResult] = useState(null);

  // Paint Calculator
  const [paintArea, setPaintArea] = useState('');
  const [paintCoats, setPaintCoats] = useState('2');
  const [paintCoverage, setPaintCoverage] = useState('350');
  const [paintResult, setPaintResult] = useState(null);

  // Asphalt Calculator State
  const [asphaltLength, setAsphaltLength] = useState('');
  const [asphaltWidth, setAsphaltWidth] = useState('');
  const [asphaltDepth, setAsphaltDepth] = useState('');
  const [asphaltDensity, setAsphaltDensity] = useState('145'); // lbs per cubic foot
  const [asphaltResult, setAsphaltResult] = useState(null);

  // Rebar Calculator State
  const [rebarLength, setRebarLength] = useState('');
  const [rebarWidth, setRebarWidth] = useState('');
  const [rebarSpacing, setRebarSpacing] = useState('12');
  const [rebarSize, setRebarSize] = useState('#4');
  const [rebarResult, setRebarResult] = useState(null);

  // Aggregate Calculator State
  const [aggLength, setAggLength] = useState('');
  const [aggWidth, setAggWidth] = useState('');
  const [aggDepth, setAggDepth] = useState('');
  const [aggDensity, setAggDensity] = useState('1.4'); // tons per CY
  const [aggResult, setAggResult] = useState(null);

  // Copied state
  const [copied, setCopied] = useState(false);

  // Pythagorean Calculation
  const calculatePythagorean = () => {
    const a = parseFloat(pythA);
    const b = parseFloat(pythB);
    const c = parseFloat(pythC);

    if (pythSolveFor === 'c' && a && b) {
      const result = Math.sqrt(a * a + b * b);
      setPythC(result.toFixed(4));
      toast.success(`Hypotenuse (c) = ${result.toFixed(4)}`);
    } else if (pythSolveFor === 'a' && b && c) {
      const result = Math.sqrt(c * c - b * b);
      setPythA(result.toFixed(4));
      toast.success(`Side (a) = ${result.toFixed(4)}`);
    } else if (pythSolveFor === 'b' && a && c) {
      const result = Math.sqrt(c * c - a * a);
      setPythB(result.toFixed(4));
      toast.success(`Side (b) = ${result.toFixed(4)}`);
    } else {
      toast.error('Enter two values to solve for the third');
    }
  };

  // Slope Calculation
  const calculateSlope = () => {
    const rise = parseFloat(slopeRise);
    const run = parseFloat(slopeRun);
    if (rise && run) {
      setSlopeResult({
        percent: ((rise / run) * 100).toFixed(2),
        ratio: `${rise}:${run}`,
        angle: (Math.atan(rise / run) * (180 / Math.PI)).toFixed(2),
        pitchPer12: ((rise / run) * 12).toFixed(2)
      });
      toast.success('Slope calculated');
    }
  };

  // Stair Calculation
  const calculateStairs = () => {
    const totalRise = parseFloat(stairTotalRise);
    const riserH = parseFloat(stairRiserHeight);
    const treadD = parseFloat(stairTreadDepth);

    if (totalRise && riserH) {
      const numRisers = Math.ceil(totalRise / riserH);
      const actualRiser = totalRise / numRisers;
      const numTreads = numRisers - 1;
      const totalRun = numTreads * treadD;
      const stringerLength = Math.sqrt(Math.pow(totalRise, 2) + Math.pow(totalRun, 2));
      const riseRun = actualRiser + treadD;
      const codeCompliant = actualRiser >= 4 && actualRiser <= 7.75 && treadD >= 10 && riseRun >= 17 && riseRun <= 18;

      setStairResult({
        numRisers,
        actualRiser: actualRiser.toFixed(3),
        numTreads,
        totalRun: totalRun.toFixed(2),
        stringerLength: stringerLength.toFixed(2),
        riseRun: riseRun.toFixed(2),
        codeCompliant
      });
      toast.success('Stairs calculated');
    }
  };

  // Ramp Calculation
  const calculateRamp = () => {
    const rise = parseFloat(rampRise);
    const ratio = parseFloat(rampRatio);
    if (rise && ratio) {
      const run = rise * ratio;
      const length = Math.sqrt(Math.pow(rise, 2) + Math.pow(run, 2));
      const slope = (rise / run) * 100;
      const angle = Math.atan(rise / run) * (180 / Math.PI);

      setRampResult({
        run: run.toFixed(2),
        length: length.toFixed(2),
        slope: slope.toFixed(2),
        angle: angle.toFixed(2),
        adaCompliant: ratio >= 12
      });
      toast.success('Ramp calculated');
    }
  };

  // Concrete Calculation
  const calculateConcrete = () => {
    let cubicFeet = 0;
    if (concreteType === 'slab' || concreteType === 'footing') {
      const l = parseFloat(concreteLength);
      const w = parseFloat(concreteWidth);
      const d = parseFloat(concreteDepth) / 12;
      if (l && w && d) cubicFeet = l * w * d;
    } else if (concreteType === 'column') {
      const d = parseFloat(concreteDiameter);
      const h = parseFloat(concreteHeight);
      if (d && h) {
        const radius = d / 24;
        cubicFeet = Math.PI * radius * radius * h;
      }
    } else if (concreteType === 'wall') {
      const l = parseFloat(concreteLength);
      const h = parseFloat(concreteHeight);
      const t = parseFloat(concreteDepth) / 12;
      if (l && h && t) cubicFeet = l * h * t;
    }

    if (cubicFeet > 0) {
      setConcreteResult({
        cubicFeet: cubicFeet.toFixed(2),
        cubicYards: (cubicFeet / 27).toFixed(3),
        bags60lb: Math.ceil(cubicFeet * 2.2),
        bags80lb: Math.ceil(cubicFeet * 1.65),
        with10Waste: ((cubicFeet / 27) * 1.1).toFixed(3)
      });
      toast.success('Concrete calculated');
    }
  };

  // Unit Conversion
  const conversionRates = {
    feet: { meters: 0.3048, inches: 12, yards: 0.333333, centimeters: 30.48, millimeters: 304.8 },
    meters: { feet: 3.28084, inches: 39.3701, yards: 1.09361, centimeters: 100, millimeters: 1000 },
    inches: { feet: 0.0833333, meters: 0.0254, yards: 0.0277778, centimeters: 2.54, millimeters: 25.4 },
    yards: { feet: 3, meters: 0.9144, inches: 36, centimeters: 91.44, millimeters: 914.4 },
    pounds: { kilograms: 0.453592, tons: 0.0005, metricTons: 0.000453592, ounces: 16 },
    kilograms: { pounds: 2.20462, tons: 0.00110231, metricTons: 0.001, ounces: 35.274 },
    tons: { pounds: 2000, kilograms: 907.185, metricTons: 0.907185, ounces: 32000 },
    cubicYards: { cubicFeet: 27, cubicMeters: 0.764555, gallons: 201.974 },
    cubicFeet: { cubicYards: 0.037037, cubicMeters: 0.0283168, gallons: 7.48052 },
    gallons: { cubicYards: 0.00495113, cubicFeet: 0.133681, liters: 3.78541 },
    squareFeet: { squareMeters: 0.092903, squareYards: 0.111111, acres: 0.0000229568 },
    squareMeters: { squareFeet: 10.7639, squareYards: 1.19599, acres: 0.000247105 },
    acres: { squareFeet: 43560, squareMeters: 4046.86, squareYards: 4840 }
  };

  const calculateConversion = () => {
    const value = parseFloat(convertValue);
    if (!value) return;
    if (convertFrom === convertTo) {
      setConvertResult(value.toFixed(4));
      return;
    }
    const rate = conversionRates[convertFrom]?.[convertTo];
    if (rate) {
      setConvertResult((value * rate).toFixed(4));
      toast.success('Converted');
    }
  };

  // Board Feet Calculation
  const calculateBoardFeet = () => {
    const t = parseFloat(bfThickness);
    const w = parseFloat(bfWidth);
    const l = parseFloat(bfLength);
    const qty = parseInt(bfQuantity) || 1;
    if (t && w && l) {
      const bf = (t * w * l) / 144;
      setBfResult({
        single: bf.toFixed(3),
        total: (bf * qty).toFixed(3),
        linearFeet: ((bf * qty * 12) / (t * w)).toFixed(2)
      });
      toast.success('Board feet calculated');
    }
  };

  // Circle Calculation
  const calculateCircle = () => {
    const value = parseFloat(circleInput);
    if (!value) return;
    let radius;
    if (circleInputType === 'radius') radius = value;
    else if (circleInputType === 'diameter') radius = value / 2;
    else if (circleInputType === 'circumference') radius = value / (2 * Math.PI);

    setCircleResult({
      radius: radius.toFixed(4),
      diameter: (radius * 2).toFixed(4),
      circumference: (2 * Math.PI * radius).toFixed(4),
      area: (Math.PI * radius * radius).toFixed(4)
    });
    toast.success('Circle calculated');
  };

  // Brick/Block Calculation
  const calculateBricks = () => {
    const length = parseFloat(wallLength);
    const height = parseFloat(wallHeight);
    if (!length || !height) return;

    const sqft = length * height;
    let bricksPerSqFt, mortarBags;

    switch (brickType) {
      case 'standard':
        bricksPerSqFt = 6.75;
        mortarBags = sqft / 35;
        break;
      case 'modular':
        bricksPerSqFt = 7;
        mortarBags = sqft / 35;
        break;
      case 'cmu8':
        bricksPerSqFt = 1.125;
        mortarBags = sqft / 12;
        break;
      case 'cmu12':
        bricksPerSqFt = 1.125;
        mortarBags = sqft / 10;
        break;
      default:
        bricksPerSqFt = 6.75;
        mortarBags = sqft / 35;
    }

    setBrickResult({
      sqft: sqft.toFixed(0),
      units: Math.ceil(sqft * bricksPerSqFt * 1.05),
      mortarBags: Math.ceil(mortarBags),
      with5Waste: Math.ceil(sqft * bricksPerSqFt * 1.1)
    });
    toast.success('Bricks calculated');
  };

  // Paint Calculation
  const calculatePaint = () => {
    const area = parseFloat(paintArea);
    const coats = parseInt(paintCoats);
    const coverage = parseFloat(paintCoverage);
    if (area && coats && coverage) {
      const gallons = (area * coats) / coverage;
      setPaintResult({
        gallons: gallons.toFixed(2),
        quarts: (gallons * 4).toFixed(1),
        with10Waste: (gallons * 1.1).toFixed(2)
      });
      toast.success('Paint calculated');
    }
  };

  // Asphalt Calculation
  const calculateAsphalt = () => {
    const l = parseFloat(asphaltLength);
    const w = parseFloat(asphaltWidth);
    const d = parseFloat(asphaltDepth); // in inches
    const density = parseFloat(asphaltDensity);

    if (!l || !w || !d) {
      toast.error('Please fill in all dimensions');
      return;
    }

    const sqft = l * w;
    const cubicFeet = sqft * (d / 12); // convert depth from inches to feet
    const cubicYards = cubicFeet / 27;
    const tons = (cubicFeet * density) / 2000;

    setAsphaltResult({
      sqft: sqft.toFixed(0),
      cubicYards: cubicYards.toFixed(2),
      tons: tons.toFixed(2),
      with5Waste: (tons * 1.05).toFixed(2)
    });
    toast.success('Asphalt tonnage calculated');
  };

  // Rebar Calculation
  const calculateRebar = () => {
    const l = parseFloat(rebarLength);
    const w = parseFloat(rebarWidth);
    const spacing = parseFloat(rebarSpacing); // in inches

    if (!l || !w || !spacing) {
      toast.error('Please fill in all dimensions');
      return;
    }

    // Rebar weights (lbs per foot)
    const rebarWeights = {
      '#3': 0.376,
      '#4': 0.668,
      '#5': 1.043,
      '#6': 1.502,
      '#7': 2.044,
      '#8': 2.670
    };

    const weight = rebarWeights[rebarSize] || 0.668;
    
    // Calculate number of bars needed in each direction
    const spacingFt = spacing / 12;
    const barsLengthwise = Math.ceil(w / spacingFt) + 1;
    const barsWidthwise = Math.ceil(l / spacingFt) + 1;
    
    // Total linear feet of rebar
    const linearFeetLengthwise = barsLengthwise * l;
    const linearFeetWidthwise = barsWidthwise * w;
    const totalLinearFeet = linearFeetLengthwise + linearFeetWidthwise;
    
    // Calculate number of 20' bars needed
    const barsNeeded = Math.ceil(totalLinearFeet / 20);
    const totalWeight = totalLinearFeet * weight;

    setRebarResult({
      barsLengthwise,
      barsWidthwise,
      totalBars: barsNeeded,
      linearFeet: totalLinearFeet.toFixed(0),
      weight: totalWeight.toFixed(0),
      tons: (totalWeight / 2000).toFixed(3)
    });
    toast.success('Rebar calculated');
  };

  // Aggregate Calculation
  const calculateAggregate = () => {
    const l = parseFloat(aggLength);
    const w = parseFloat(aggWidth);
    const d = parseFloat(aggDepth); // in inches
    const density = parseFloat(aggDensity); // tons per CY

    if (!l || !w || !d) {
      toast.error('Please fill in all dimensions');
      return;
    }

    const sqft = l * w;
    const cubicFeet = sqft * (d / 12);
    const cubicYards = cubicFeet / 27;
    const tons = cubicYards * density;

    setAggResult({
      sqft: sqft.toFixed(0),
      cubicYards: cubicYards.toFixed(2),
      tons: tons.toFixed(2),
      with5Waste: (tons * 1.05).toFixed(2)
    });
    toast.success('Aggregate calculated');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Calculator className="w-6 h-6 text-primary" />
          Engineering Tools
        </h2>
        <p className="text-muted-foreground mt-1">Professional construction calculations</p>
      </div>

      <Tabs defaultValue="geometry" className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6 h-auto">
          <TabsTrigger value="geometry" className="flex flex-col py-2">
            <Triangle className="w-4 h-4 mb-1" />
            <span className="text-xs">Geometry</span>
          </TabsTrigger>
          <TabsTrigger value="stairs" className="flex flex-col py-2">
            <StepForward className="w-4 h-4 mb-1" />
            <span className="text-xs">Stairs/Ramps</span>
          </TabsTrigger>
          <TabsTrigger value="materials" className="flex flex-col py-2">
            <Box className="w-4 h-4 mb-1" />
            <span className="text-xs">Materials</span>
          </TabsTrigger>
          <TabsTrigger value="convert" className="flex flex-col py-2">
            <ArrowLeftRight className="w-4 h-4 mb-1" />
            <span className="text-xs">Convert</span>
          </TabsTrigger>
        </TabsList>

        {/* Geometry Tab */}
        <TabsContent value="geometry" className="space-y-4">
          {/* Pythagorean */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Triangle className="w-5 h-5 text-primary" />
                Right Angle (a² + b² = c²)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {['a', 'b', 'c'].map((side) => (
                  <div key={side} className="space-y-1">
                    <Label className="text-xs flex items-center gap-1">
                      Side {side.toUpperCase()}
                      {pythSolveFor === side && <span className="text-primary">(solving)</span>}
                    </Label>
                    <Input
                      type="number"
                      value={side === 'a' ? pythA : side === 'b' ? pythB : pythC}
                      onChange={(e) => {
                        if (side === 'a') setPythA(e.target.value);
                        else if (side === 'b') setPythB(e.target.value);
                        else setPythC(e.target.value);
                      }}
                      disabled={pythSolveFor === side}
                      placeholder={side === 'c' ? 'Hypotenuse' : `Side ${side}`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Solve for:</span>
                {['a', 'b', 'c'].map((side) => (
                  <Button
                    key={side}
                    size="sm"
                    variant={pythSolveFor === side ? 'default' : 'outline'}
                    onClick={() => setPythSolveFor(side)}
                  >
                    {side.toUpperCase()}
                  </Button>
                ))}
                <Button onClick={calculatePythagorean} className="ml-auto">Calculate</Button>
              </div>
            </CardContent>
          </Card>

          {/* Slope */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Mountain className="w-5 h-5 text-primary" />
                Slope / Grade Calculator
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Rise (vertical)</Label>
                  <Input type="number" value={slopeRise} onChange={(e) => setSlopeRise(e.target.value)} placeholder="Enter rise" />
                </div>
                <div className="space-y-1">
                  <Label>Run (horizontal)</Label>
                  <Input type="number" value={slopeRun} onChange={(e) => setSlopeRun(e.target.value)} placeholder="Enter run" />
                </div>
              </div>
              <Button onClick={calculateSlope} className="w-full">Calculate Slope</Button>
              {slopeResult && (
                <div className="grid grid-cols-4 gap-3">
                  <ResultCard label="Percent" value={slopeResult.percent} unit="%" />
                  <ResultCard label="Ratio" value={slopeResult.ratio} color="accent" />
                  <ResultCard label="Angle" value={slopeResult.angle} unit="°" color="success" />
                  <ResultCard label="Pitch/12" value={slopeResult.pitchPer12} color="orange-500" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Circle */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Circle className="w-5 h-5 text-primary" />
                Circle / Arc Calculator
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Input Type</Label>
                  <Select value={circleInputType} onValueChange={setCircleInputType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="radius">Radius</SelectItem>
                      <SelectItem value="diameter">Diameter</SelectItem>
                      <SelectItem value="circumference">Circumference</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>{circleInputType.charAt(0).toUpperCase() + circleInputType.slice(1)}</Label>
                  <Input type="number" value={circleInput} onChange={(e) => setCircleInput(e.target.value)} placeholder="Enter value" />
                </div>
              </div>
              <Button onClick={calculateCircle} className="w-full">Calculate</Button>
              {circleResult && (
                <div className="grid grid-cols-4 gap-3">
                  <ResultCard label="Radius" value={circleResult.radius} />
                  <ResultCard label="Diameter" value={circleResult.diameter} color="accent" />
                  <ResultCard label="Circumference" value={circleResult.circumference} color="success" />
                  <ResultCard label="Area" value={circleResult.area} color="orange-500" />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stairs & Ramps Tab */}
        <TabsContent value="stairs" className="space-y-4">
          {/* Stair Calculator */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <StepForward className="w-5 h-5 text-primary" />
                Stair Calculator (IBC/IRC Code)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label>Total Rise (in)</Label>
                  <Input type="number" value={stairTotalRise} onChange={(e) => setStairTotalRise(e.target.value)} placeholder="Floor to floor height" />
                </div>
                <div className="space-y-1">
                  <Label>Target Riser (in)</Label>
                  <Input type="number" value={stairRiserHeight} onChange={(e) => setStairRiserHeight(e.target.value)} placeholder="7" />
                </div>
                <div className="space-y-1">
                  <Label>Tread Depth (in)</Label>
                  <Input type="number" value={stairTreadDepth} onChange={(e) => setStairTreadDepth(e.target.value)} placeholder="11" />
                </div>
              </div>
              <Button onClick={calculateStairs} className="w-full">Calculate Stairs</Button>
              {stairResult && (
                <>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                    <ResultCard label="# Risers" value={stairResult.numRisers} />
                    <ResultCard label="Actual Riser" value={stairResult.actualRiser} unit="in" color="accent" />
                    <ResultCard label="# Treads" value={stairResult.numTreads} />
                    <ResultCard label="Total Run" value={stairResult.totalRun} unit="in" color="success" />
                    <ResultCard label="Stringer" value={stairResult.stringerLength} unit="in" color="orange-500" />
                    <ResultCard label="Rise+Run" value={stairResult.riseRun} unit="in" color="purple-500" />
                  </div>
                  <div className={`p-3 rounded-lg ${stairResult.codeCompliant ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    <p className="text-sm font-medium">
                      {stairResult.codeCompliant ? '✓ Code Compliant' : '✗ Check Code Requirements'}
                    </p>
                    <p className="text-xs">IRC: Riser 4&quot;-7.75&quot;, Tread ≥10&quot;, Rise+Run 17&quot;-18&quot;</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Ramp Calculator */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <ArrowUp className="w-5 h-5 text-primary rotate-45" />
                ADA Ramp Calculator
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Rise (in)</Label>
                  <Input type="number" value={rampRise} onChange={(e) => setRampRise(e.target.value)} placeholder="Height change" />
                </div>
                <div className="space-y-1">
                  <Label>Slope Ratio (1:X)</Label>
                  <Select value={rampRatio} onValueChange={setRampRatio}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="8">1:8 (max for existing)</SelectItem>
                      <SelectItem value="10">1:10 (max for new)</SelectItem>
                      <SelectItem value="12">1:12 (ADA preferred)</SelectItem>
                      <SelectItem value="16">1:16 (easier access)</SelectItem>
                      <SelectItem value="20">1:20 (gentle slope)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={calculateRamp} className="w-full">Calculate Ramp</Button>
              {rampResult && (
                <>
                  <div className="grid grid-cols-4 gap-3">
                    <ResultCard label="Run Required" value={rampResult.run} unit="in" />
                    <ResultCard label="Ramp Length" value={rampResult.length} unit="in" color="accent" />
                    <ResultCard label="Slope" value={rampResult.slope} unit="%" color="success" />
                    <ResultCard label="Angle" value={rampResult.angle} unit="°" color="orange-500" />
                  </div>
                  <div className={`p-3 rounded-lg ${rampResult.adaCompliant ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    <p className="text-sm font-medium">
                      {rampResult.adaCompliant ? '✓ ADA Compliant (1:12 or less)' : '⚠ Steeper than ADA standard (1:12)'}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Materials Tab */}
        <TabsContent value="materials" className="space-y-4">
          {/* Concrete */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Box className="w-5 h-5 text-primary" />
                Concrete Volume
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2 mb-4">
                {[
                  { value: 'slab', label: 'Slab' },
                  { value: 'wall', label: 'Wall' },
                  { value: 'footing', label: 'Footing' },
                  { value: 'column', label: 'Column' }
                ].map((type) => (
                  <Button
                    key={type.value}
                    variant={concreteType === type.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setConcreteType(type.value)}
                  >
                    {type.label}
                  </Button>
                ))}
              </div>
              {(concreteType === 'slab' || concreteType === 'footing') && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label>Length (ft)</Label>
                    <Input type="number" value={concreteLength} onChange={(e) => setConcreteLength(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label>Width (ft)</Label>
                    <Input type="number" value={concreteWidth} onChange={(e) => setConcreteWidth(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label>Depth (in)</Label>
                    <Input type="number" value={concreteDepth} onChange={(e) => setConcreteDepth(e.target.value)} />
                  </div>
                </div>
              )}
              {concreteType === 'wall' && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label>Length (ft)</Label>
                    <Input type="number" value={concreteLength} onChange={(e) => setConcreteLength(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label>Height (ft)</Label>
                    <Input type="number" value={concreteHeight} onChange={(e) => setConcreteHeight(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label>Thickness (in)</Label>
                    <Input type="number" value={concreteDepth} onChange={(e) => setConcreteDepth(e.target.value)} />
                  </div>
                </div>
              )}
              {concreteType === 'column' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label>Diameter (in)</Label>
                    <Input type="number" value={concreteDiameter} onChange={(e) => setConcreteDiameter(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label>Height (ft)</Label>
                    <Input type="number" value={concreteHeight} onChange={(e) => setConcreteHeight(e.target.value)} />
                  </div>
                </div>
              )}
              <Button onClick={calculateConcrete} className="w-full">Calculate</Button>
              {concreteResult && (
                <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                  <ResultCard label="Cu. Feet" value={concreteResult.cubicFeet} />
                  <ResultCard label="Cu. Yards" value={concreteResult.cubicYards} color="accent" />
                  <ResultCard label="+10% Waste" value={concreteResult.with10Waste} unit="cy" color="success" />
                  <ResultCard label="60lb Bags" value={concreteResult.bags60lb} color="orange-500" />
                  <ResultCard label="80lb Bags" value={concreteResult.bags80lb} color="purple-500" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Board Feet */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Layers className="w-5 h-5 text-primary" />
                Board Feet Calculator
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-1">
                  <Label>Thickness (in)</Label>
                  <Input type="number" value={bfThickness} onChange={(e) => setBfThickness(e.target.value)} placeholder="1" />
                </div>
                <div className="space-y-1">
                  <Label>Width (in)</Label>
                  <Input type="number" value={bfWidth} onChange={(e) => setBfWidth(e.target.value)} placeholder="6" />
                </div>
                <div className="space-y-1">
                  <Label>Length (ft)</Label>
                  <Input type="number" value={bfLength} onChange={(e) => setBfLength(e.target.value)} placeholder="8" />
                </div>
                <div className="space-y-1">
                  <Label>Quantity</Label>
                  <Input type="number" value={bfQuantity} onChange={(e) => setBfQuantity(e.target.value)} placeholder="1" min="1" />
                </div>
              </div>
              <Button onClick={calculateBoardFeet} className="w-full">Calculate</Button>
              {bfResult && (
                <div className="grid grid-cols-3 gap-3">
                  <ResultCard label="BF Each" value={bfResult.single} />
                  <ResultCard label="Total BF" value={bfResult.total} color="accent" />
                  <ResultCard label="Linear Feet" value={bfResult.linearFeet} color="success" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Brick/Block */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Grid3X3 className="w-5 h-5 text-primary" />
                Brick / Block Calculator
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label>Wall Length (ft)</Label>
                  <Input type="number" value={wallLength} onChange={(e) => setWallLength(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label>Wall Height (ft)</Label>
                  <Input type="number" value={wallHeight} onChange={(e) => setWallHeight(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label>Brick/Block Type</Label>
                  <Select value={brickType} onValueChange={setBrickType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard Brick</SelectItem>
                      <SelectItem value="modular">Modular Brick</SelectItem>
                      <SelectItem value="cmu8">8&quot; CMU Block</SelectItem>
                      <SelectItem value="cmu12">12&quot; CMU Block</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={calculateBricks} className="w-full">Calculate</Button>
              {brickResult && (
                <div className="grid grid-cols-4 gap-3">
                  <ResultCard label="Wall SF" value={brickResult.sqft} />
                  <ResultCard label="Units +5%" value={brickResult.units} color="accent" />
                  <ResultCard label="Units +10%" value={brickResult.with5Waste} color="success" />
                  <ResultCard label="Mortar Bags" value={brickResult.mortarBags} color="orange-500" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Paint */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Building className="w-5 h-5 text-primary" />
                Paint Calculator
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label>Wall Area (sq ft)</Label>
                  <Input type="number" value={paintArea} onChange={(e) => setPaintArea(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label>Coats</Label>
                  <Select value={paintCoats} onValueChange={setPaintCoats}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 Coat</SelectItem>
                      <SelectItem value="2">2 Coats</SelectItem>
                      <SelectItem value="3">3 Coats</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Coverage (sf/gal)</Label>
                  <Input type="number" value={paintCoverage} onChange={(e) => setPaintCoverage(e.target.value)} />
                </div>
              </div>
              <Button onClick={calculatePaint} className="w-full">Calculate</Button>
              {paintResult && (
                <div className="grid grid-cols-3 gap-3">
                  <ResultCard label="Gallons" value={paintResult.gallons} />
                  <ResultCard label="Quarts" value={paintResult.quarts} color="accent" />
                  <ResultCard label="+10% Waste" value={paintResult.with10Waste} unit="gal" color="success" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Asphalt Calculator */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Mountain className="w-5 h-5 text-primary" />
                Asphalt Tonnage Calculator
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Calculate asphalt (hot mix) requirements for paving projects.
              </p>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-1">
                  <Label>Length (ft)</Label>
                  <Input 
                    type="number" 
                    value={asphaltLength} 
                    onChange={(e) => setAsphaltLength(e.target.value)} 
                    placeholder="100"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Width (ft)</Label>
                  <Input 
                    type="number" 
                    value={asphaltWidth} 
                    onChange={(e) => setAsphaltWidth(e.target.value)} 
                    placeholder="20"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Depth (in)</Label>
                  <Input 
                    type="number" 
                    value={asphaltDepth} 
                    onChange={(e) => setAsphaltDepth(e.target.value)} 
                    placeholder="2"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Density (lbs/cf)</Label>
                  <Input 
                    type="number" 
                    value={asphaltDensity} 
                    onChange={(e) => setAsphaltDensity(e.target.value)} 
                    placeholder="145"
                  />
                </div>
              </div>
              <Button onClick={calculateAsphalt} className="w-full">Calculate Asphalt</Button>
              {asphaltResult && (
                <div className="grid grid-cols-4 gap-3">
                  <ResultCard label="Sq. Feet" value={asphaltResult.sqft} />
                  <ResultCard label="Cu. Yards" value={asphaltResult.cubicYards} color="accent" />
                  <ResultCard label="Tons" value={asphaltResult.tons} color="success" />
                  <ResultCard label="+5% Waste" value={asphaltResult.with5Waste} unit="tons" color="orange-500" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Rebar Calculator */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Grid3X3 className="w-5 h-5 text-primary" />
                Rebar Calculator
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Calculate rebar requirements for concrete slabs with grid layout.
              </p>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-1">
                  <Label>Length (ft)</Label>
                  <Input 
                    type="number" 
                    value={rebarLength} 
                    onChange={(e) => setRebarLength(e.target.value)} 
                    placeholder="20"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Width (ft)</Label>
                  <Input 
                    type="number" 
                    value={rebarWidth} 
                    onChange={(e) => setRebarWidth(e.target.value)} 
                    placeholder="20"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Spacing (in)</Label>
                  <Input 
                    type="number" 
                    value={rebarSpacing} 
                    onChange={(e) => setRebarSpacing(e.target.value)} 
                    placeholder="12"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Rebar Size</Label>
                  <Select value={rebarSize} onValueChange={setRebarSize}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="#3">#3 (3/8&quot; - 0.376 lb/ft)</SelectItem>
                      <SelectItem value="#4">#4 (1/2&quot; - 0.668 lb/ft)</SelectItem>
                      <SelectItem value="#5">#5 (5/8&quot; - 1.043 lb/ft)</SelectItem>
                      <SelectItem value="#6">#6 (3/4&quot; - 1.502 lb/ft)</SelectItem>
                      <SelectItem value="#7">#7 (7/8&quot; - 2.044 lb/ft)</SelectItem>
                      <SelectItem value="#8">#8 (1&quot; - 2.670 lb/ft)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={calculateRebar} className="w-full">Calculate Rebar</Button>
              {rebarResult && (
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  <ResultCard label="Long Bars" value={rebarResult.barsLengthwise} />
                  <ResultCard label="Wide Bars" value={rebarResult.barsWidthwise} />
                  <ResultCard label="20' Bars" value={rebarResult.totalBars} color="accent" />
                  <ResultCard label="Linear Ft" value={rebarResult.linearFeet} color="success" />
                  <ResultCard label="Weight" value={rebarResult.weight} unit="lbs" color="orange-500" />
                  <ResultCard label="Tons" value={rebarResult.tons} color="purple-500" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Aggregate Calculator */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Weight className="w-5 h-5 text-primary" />
                Aggregate/Base Calculator
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Calculate gravel, crushed stone, or base material requirements.
              </p>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-1">
                  <Label>Length (ft)</Label>
                  <Input 
                    type="number" 
                    value={aggLength} 
                    onChange={(e) => setAggLength(e.target.value)} 
                    placeholder="100"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Width (ft)</Label>
                  <Input 
                    type="number" 
                    value={aggWidth} 
                    onChange={(e) => setAggWidth(e.target.value)} 
                    placeholder="20"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Depth (in)</Label>
                  <Input 
                    type="number" 
                    value={aggDepth} 
                    onChange={(e) => setAggDepth(e.target.value)} 
                    placeholder="4"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Density (tons/CY)</Label>
                  <Input 
                    type="number" 
                    value={aggDensity} 
                    onChange={(e) => setAggDensity(e.target.value)} 
                    placeholder="1.4"
                    step="0.1"
                  />
                </div>
              </div>
              <Button onClick={calculateAggregate} className="w-full">Calculate Aggregate</Button>
              {aggResult && (
                <div className="grid grid-cols-4 gap-3">
                  <ResultCard label="Sq. Feet" value={aggResult.sqft} />
                  <ResultCard label="Cu. Yards" value={aggResult.cubicYards} color="accent" />
                  <ResultCard label="Tons" value={aggResult.tons} color="success" />
                  <ResultCard label="+5% Waste" value={aggResult.with5Waste} unit="tons" color="orange-500" />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Convert Tab */}
        <TabsContent value="convert">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <ArrowLeftRight className="w-5 h-5 text-primary" />
                Unit Converter
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <Label>Value</Label>
                  <Input type="number" value={convertValue} onChange={(e) => setConvertValue(e.target.value)} placeholder="Enter value" />
                </div>
                <div className="space-y-1">
                  <Label>From</Label>
                  <Select value={convertFrom} onValueChange={setConvertFrom}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="feet">Feet</SelectItem>
                      <SelectItem value="meters">Meters</SelectItem>
                      <SelectItem value="inches">Inches</SelectItem>
                      <SelectItem value="yards">Yards</SelectItem>
                      <SelectItem value="pounds">Pounds</SelectItem>
                      <SelectItem value="kilograms">Kilograms</SelectItem>
                      <SelectItem value="tons">Tons</SelectItem>
                      <SelectItem value="cubicYards">Cubic Yards</SelectItem>
                      <SelectItem value="cubicFeet">Cubic Feet</SelectItem>
                      <SelectItem value="gallons">Gallons</SelectItem>
                      <SelectItem value="squareFeet">Square Feet</SelectItem>
                      <SelectItem value="squareMeters">Square Meters</SelectItem>
                      <SelectItem value="acres">Acres</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>To</Label>
                  <Select value={convertTo} onValueChange={setConvertTo}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="feet">Feet</SelectItem>
                      <SelectItem value="meters">Meters</SelectItem>
                      <SelectItem value="inches">Inches</SelectItem>
                      <SelectItem value="yards">Yards</SelectItem>
                      <SelectItem value="pounds">Pounds</SelectItem>
                      <SelectItem value="kilograms">Kilograms</SelectItem>
                      <SelectItem value="tons">Tons</SelectItem>
                      <SelectItem value="metricTons">Metric Tons</SelectItem>
                      <SelectItem value="cubicYards">Cubic Yards</SelectItem>
                      <SelectItem value="cubicFeet">Cubic Feet</SelectItem>
                      <SelectItem value="cubicMeters">Cubic Meters</SelectItem>
                      <SelectItem value="gallons">Gallons</SelectItem>
                      <SelectItem value="liters">Liters</SelectItem>
                      <SelectItem value="squareFeet">Square Feet</SelectItem>
                      <SelectItem value="squareMeters">Square Meters</SelectItem>
                      <SelectItem value="squareYards">Square Yards</SelectItem>
                      <SelectItem value="acres">Acres</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>&nbsp;</Label>
                  <Button onClick={calculateConversion} className="w-full">Convert</Button>
                </div>
              </div>

              {convertResult && (
                <div className="bg-primary/10 rounded-lg p-6 text-center">
                  <p className="text-sm text-muted-foreground mb-2">Result</p>
                  <div className="flex items-center justify-center gap-3">
                    <p className="text-3xl font-bold text-primary">{convertResult}</p>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(convertResult)}>
                      {copied ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {convertValue} {convertFrom} = {convertResult} {convertTo}
                  </p>
                </div>
              )}

              {/* Quick Reference */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                <div className="bg-muted/30 rounded-lg p-3 text-xs">
                  <p className="font-semibold mb-1">Length</p>
                  <p>1 ft = 0.3048 m</p>
                  <p>1 yd = 3 ft = 36 in</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3 text-xs">
                  <p className="font-semibold mb-1">Weight</p>
                  <p>1 ton = 2000 lbs</p>
                  <p>1 kg = 2.205 lbs</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3 text-xs">
                  <p className="font-semibold mb-1">Volume</p>
                  <p>1 cy = 27 cu ft</p>
                  <p>1 gal = 3.785 L</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3 text-xs">
                  <p className="font-semibold mb-1">Area</p>
                  <p>1 acre = 43,560 sf</p>
                  <p>1 sq m = 10.76 sf</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EngineeringTools;
